import React, { useState, useEffect } from "react";
import { getEmail } from "../../../DecodeToken/DecodeToken";
import {
  generateReferralCode,
  geRreferralAmoutWithdraw,
  getReferralsData,
  getUpiDetails,
  referralAmoutWithdraw,
} from "../../../ApiServices/ApiServices";
import { FaCog, FaUniversity } from "react-icons/fa";
import CodeQueueSpinner, { handleError, handleSuccess } from "../../../Utils"; // Import your custom functions
import UserUPIID from "./UserUPIID";
import ReferralAmoutWithdrawRequest from "./ReferralAmoutWithdrawRequest";

function Rewards() {
  const email = getEmail();
  const [referralCode, setReferralCode] = useState("");
  const [selectedSection, setSelectedSection] = useState("referrals");
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUPIConnected, setIsUPIConnected] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const [upiId, setUpiId] = useState("");
  const [userName, setUserName] = useState("");
  const [withdrawRequestVisible, setWithdrawRequestVisible] = useState(false);
  const [lastRequestedWithdrawalAmount, setLastRequestedWithdrawalAmount] =
    useState(0);
  const [lastRequestedWithdrawalStatus, setLastRequestedWithdrawalStatus] =
    useState("");

  const fetchUPIDetails = async () => {
    try {
      const res = await getUpiDetails();
      setUpiId(res?.upiData?.upi_id);
      setUserName(res?.upiData?.upi_user);
      if (res?.upiData?.upi_id) {
        setIsUPIConnected(true);
      }
    } catch (error) {
      return handleError(error?.response?.data?.message);
    }
  };

  const fetchReferralRequestedAmount = async () => {
    try {
      const res = await geRreferralAmoutWithdraw();
      setLastRequestedWithdrawalAmount(res?.requestAmount);
      setLastRequestedWithdrawalStatus(res?.requestStatus);
    } catch (error) {
      return handleError(error?.response?.data?.message);
    }
  };

  const referralsDetails = async () => {
    try {
      //find referral code
      const response = await getReferralsData(email);
      setReferralCode(response?.referralCode);
      setReferralData(response?.referralData);
    } catch (error) {
      return handleError(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Dashboard - Reward/Referrals";
    if (email) {
      referralsDetails();
      fetchUPIDetails();
      fetchReferralRequestedAmount();
    }
  }, [email, referralCode]);

  const handleReferralCode = async () => {
    try {
      const response = await generateReferralCode(email);
      handleSuccess(response.message);
      setReferralCode(response.referralCode);
    } catch (error) {
      return handleError(error?.response?.data?.message);
    }
  };

  const handleSectionChange = (section) => {
    setSelectedSection(section);
  };

  // Function to handle copying the referral link or referral code to clipboard
  const handleCopyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => handleSuccess("Copied to clipboard"))
      .catch(() => handleError("Failed to copy link"));
  };

  const handleWithdrawClick = ()=>{
    if(isUPIConnected){
      setWithdrawRequestVisible(true);
    }else{
      setIsVisible(true);
    }
  }

  return (
    <div className="">
      {/* First Container: Heading, Buttons, and HR */}
      <div className="flex flex-col items-start mb-1">
        <div className="w-full flex justify-between">
          <h1 className="text-3xl font-semibold mb-2">Rewards</h1>
          {isUPIConnected ? (
            <div
              className="flex items-center mt-2 sm:mt-0 cursor-pointer hover:bg-slate-300"
              onClick={setIsVisible}
            >
              <div className="flex items-center space-x-2 border-gray-400 p-2">
                <FaUniversity className="w-8 h-8 flex items-center justify-center" />
                <div>
                  <div className="flex items-center space-x-1">
                    <span className="text-sm">Payout</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="text-green-500 text-sm">Connected</div>
                    <div className="w-4 h-4 bg-green-500 text-white text-xs rounded-full flex items-center justify-center">
                      ✔
                    </div>
                  </div>
                </div>
              </div>
              {/* <FaCog className="w-10 h-10 rounded-full border flex items-center justify-center ml-2 p-2" /> */}
            </div>
          ) : (
            <div className="mt-2 mb-4">
              <a
                onClick={setIsVisible}
                className="bg-black text-white py-2 px-10 rounded-lg hover:cursor-pointer"
              >
                Payout
              </a>
            </div>
          )}
        </div>
        {/* Reduced gap */}
        <div className="flex gap-4 mb-4">
          <div
            className={`secondary-btn py-2 px-4 rounded cursor-pointer ${
              selectedSection === "referrals" ? "bg-gray-300" : ""
            }`}
            onClick={() => handleSectionChange("referrals")}
          >
            Referrals
          </div>
          <div
            className={`secondary-btn py-2 px-6 rounded cursor-pointer ${
              selectedSection === "rewards" ? "bg-gray-300" : ""
            }`}
            onClick={() => handleSectionChange("rewards")}
          >
            Rewards
          </div>
        </div>
        <hr className="w-full border-t border-gray-300 mb-1" />
      </div>

      {/* Referral Code Section */}
      {selectedSection === "referrals" && (
        <div className="text-center mt-1">
          {referralCode ? (
            <div className="text-left mt-1">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-x-6 mb-1">
                {/* Left Section: Invite your friends */}
                <div className="flex flex-col justify-start">
                  <p className="font-bold text-gray-800 text-3xl mt-2">
                    Invite your friends
                  </p>
                  <p className="text-gray-600  mt-3">
                    Earn{" "}
                    <span className="font-bold text-black">20/- Rupees</span>{" "}
                    instantly for every successful mentor signup!
                  </p>
                </div>
                {/* Middle Section: Share your Link */}
                <div className="flex flex-col justify-start items-start mt-2 lg:ml-16 md:mt-0">
                  <p className="text-lg font-bold text-gray-600 text-left">
                    Share your link:
                  </p>
                  <div className="relative flex items-center bg-gray-200 pl-2 rounded-md w-auto max-w-full mt-2">
                    <p className="text-gray-700 text-[15px] font-semibold truncate max-w-xs">
                      {`${window.location.origin}/signup/?ref=${referralCode}`}
                    </p>
                    <button
                      className="bg-black text-white px-1 py-1 rounded text-[15px] ml-1"
                      onClick={() =>
                        handleCopyToClipboard(
                          `${window.location.origin}/signup/?ref=${referralCode}`
                        )
                      }
                    >
                      Copy
                    </button>
                  </div>
                </div>

                {/* Right Section: Referral Code */}
                <div className="relative flex flex-col justify-start items-start md:items-end mt-2">
                  <p className="text-lg font-bold text-gray-600 text-left">
                    Referral Code :
                  </p>
                  <div className="flex items-center bg-gray-200 rounded-md w-auto max-w-xs mt-1">
                    <p className="text-gray-800 text-[15px] font-semibold px-3 py-1">
                      {referralCode}
                    </p>
                    <button
                      className="bg-black text-white px-1 py-1 rounded text-[15px]"
                      onClick={() => handleCopyToClipboard(referralCode)}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              </div>

              <div className="border border-gray-300 p-2 mt-8 rounded-md">
                <div className="flex flex-col mb-2">
                  <h2 className="text-gray-700 font-bold text-xl mb-2 text-left">
                    Referral Overview
                  </h2>
                  <hr className="border-t border-gray-300 mb-2" />
                </div>

                <div className="flex flex-wrap gap-4 mb-2 justify-center">
                  <div className="border border-gray-300 p-2 w-[200px] rounded-md">
                    <h3 className="text-2xl text-center font-semibold text-gray-700 mb-2">
                    {referralData?.referralCount || 0}
                    </h3>
                    <p className="text-gray-700 text-center">
                      Successful Referrals
                    </p>
                  </div>

                  <div className="border border-gray-300 p-2 w-[200px] rounded-md">
                    <h3 className="text-2xl text-center font-semibold text-gray-700 mb-2">
                    ₹{referralData?.totalEarning || 0}
                    </h3>
                    <p className="text-gray-700 text-center">Your Earnings</p>
                  </div>
                  <div className="border border-gray-300 p-2 w-[200px] rounded-md">
                    <h3 className="text-2xl text-center font-semibold text-gray-700 mb-2">
                    ₹{referralData?.availableBalance || 0}
                    </h3>
                    <p className="text-gray-700 text-center">Balance</p>
                    <p className="mt-2 text-center">
                      {lastRequestedWithdrawalStatus === "pending" ? (
                        <span className="text-yellow-500 text-sm">
                          Pending : {lastRequestedWithdrawalAmount}/-
                        </span>
                      ) : (
                        referralData?.availableBalance>0 &&
                        <span
                          className="bg-black text-white px-2 py-2 rounded-md hover:bg-gray-800 cursor-pointer"
                          onClick={handleWithdrawClick}
                        >
                          withdraw
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                <div className="border border-gray-300 p-2 h-[300px] overflow-y-auto rounded-md">
                  <div className="flex items-start justify-start">
                    <h3 className="text-gray-600 font-semibold text-left">
                      List of Referrals
                    </h3>
                  </div>
                  {loading ? (
                    <CodeQueueSpinner />
                  ) : (
                    <div className="mt-2">
                      {/* List of Referrals Table */}
                      <div className="overflow-x-auto mt-4">
                        <table className="min-w-full table-auto border-collapse text-xs md:text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="py-2 px-2 text-left text-xs md:text-sm font-semibold text-gray-600">
                                Reffered User
                              </th>
                              <th className="py-2 px-1 text-left text-xs md:text-sm font-semibold text-gray-600">
                                {/* <FaUserPlus className="h-5 w-5 text-gray-600 mb-1" />{" "} */}
                                {/* SignUp Icon */}
                                <span className="text-xs md:text-sm">
                                  SignUp Date
                                </span>
                              </th>
                              <th className="py-2 px-1 text-left text-xs md:text-sm font-semibold text-gray-600">
                                {/* <FaUserPlus className="h-5 w-5 text-gray-600 mb-1" />{" "} */}
                                {/* SignUp Icon */}
                                <span className="text-xs md:text-sm">
                                  SignUp As
                                </span>
                              </th>
                              <th className="py-2 px-1 text-left text-xs md:text-sm font-semibold text-gray-600">
                                Status
                              </th>
                              <th className="py-2 px-1 text-left text-xs md:text-sm font-semibold text-gray-600">
                                Amount Added
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {referralData?.referralList.map(
                              (referral, index) => (
                                <tr key={index} className="">
                                  <td className="py-2 px-2 text-xs md:text-sm text-gray-700">
                                    {referral?.referredUser}
                                  </td>
                                  <td className="py-2 px-1 text-xs md:text-sm text-gray-700">
                                    {
                                      new Date(referral?.signUpDate)
                                        .toISOString()
                                        .split("T")[0]
                                    }
                                  </td>
                                  <td className="py-2 px-1 text-xs md:text-sm text-gray-700">
                                    {referral?.signUpAs === "user"
                                      ? "Mentee"
                                      : "Mentor"}
                                  </td>
                                  <td
                                    className={`py-2 px-1 text-xs md:text-sm
                                ${
                                  referral?.status === "Successful"
                                    ? "text-green-600"
                                    : referral?.status === "Pending"
                                    ? "text-yellow-600"
                                    : "text-red-600"
                                }
                                  `}
                                  >
                                    {referral?.status}
                                  </td>
                                  <td className="py-2 px-1 text-xs md:text-sm text-gray-700">
                                    {referral?.amountAdded}/-
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="text-center mt-4">
                <p className="text-xl text-gray mb-4">
                  Welcome to the Rewards Program
                </p>
                <button
                  className="bg-black text-white py-3 px-8 rounded"
                  onClick={handleReferralCode}
                >
                  Generate Referral Code
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Rewards Section */}
      {selectedSection === "rewards" && (
        <div className="text-center p-5 ">
          <p className="text-xl text-gray bold mb-6">Rewards Coming Soon...</p>
        </div>
      )}

      {isVisible && (
        <UserUPIID
          setIsVisible={setIsVisible}
          setIsUPIConnected={setIsUPIConnected}
          upiId={upiId}
          setUpiId={setUpiId}
          userName={userName}
          setUserName={setUserName}
        />
      )}

      {withdrawRequestVisible && (
        <ReferralAmoutWithdrawRequest
          setWithdrawRequestVisible={setWithdrawRequestVisible}
          balanceToWithdraw={referralData?.availableBalance || 0}
          referralsDetails={referralsDetails}
          fetchReferralRequestedAmount={fetchReferralRequestedAmount}
        />
      )}
    </div>
  );
}

export default Rewards;
